import React, {
 createContext, useContext, useState, useEffect, useCallback
} from 'react';
import {
 useParams
} from 'react-router-dom';
import {
 applicationService
} from 'shared/api/services/BEXO';
import {
 schemaService
} from 'shared/api/services/BEXO/schema.service';
import {
 connectionService
} from 'shared/api/services/BEX';
import {
 DataBuilderV2ContextType, AppListItem
} from './types/dataBuilderV2.types';

const DataBuilderV2Context = createContext<DataBuilderV2ContextType | null>(null);

export const useDataBuilderV2 = () => {
    const context = useContext(DataBuilderV2Context);
    if (!context) {
        throw new Error('useDataBuilderV2 must be used within DataBuilderV2Provider');
    }
    return context;
};

export const DataBuilderV2Provider: React.FC<{ children: React.ReactNode }> = ({
 children
}) => {
    const {
 appId, oragID
} = useParams<{ appId: string; oragID: string }>();
    const [appName, setAppName] = useState('');
    const [appFullName, setAppFullName] = useState('');
    const [apps, setApps] = useState<AppListItem[]>([]);
    const [schemaCount, setSchemaCount] = useState(0);
    const [connectionCount, setConnectionCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchAppDetails = useCallback(async () => {
        if (!oragID || !appId) return;
        try {
            const appData = await applicationService.getApplication(oragID, appId);
            if ('name' in appData) {
                setAppName(appData.name);
                setAppFullName(appData.full_name);
            }
        } catch (error) {
            console.error('Error fetching app details:', error);
        }
    }, [oragID, appId]);

    const fetchApps = useCallback(async () => {
        if (!oragID) return;
        try {
            const res = await applicationService.listApplication(oragID);
            if (Array.isArray(res.data)) {
                setApps(res.data.map((app: any) => ({
                    app_id: app.app_id,
                    full_name: app.full_name,
                    name: app.name,
                    active: app.active,
                })));
            }
        } catch (error) {
            console.error('Error fetching apps:', error);
        }
    }, [oragID]);

    const fetchStats = useCallback(async () => {
        if (!oragID || !appId) return;
        try {
            const [schemasRes, connectionsRes] = await Promise.allSettled([
                schemaService.retrieveSchemas('*', appId, oragID),
                connectionService.getConnections(oragID, appId),
            ]);

            if (schemasRes.status === 'fulfilled') {
                const data = schemasRes.value?.data;
                setSchemaCount(Array.isArray(data) ? data.length : 0);
            }
            if (connectionsRes.status === 'fulfilled') {
                const data = connectionsRes.value;
                setConnectionCount(Array.isArray(data) ? data.length : 0);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, [oragID, appId]);

    const refreshStats = useCallback(async () => {
        await fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await Promise.all([fetchAppDetails(), fetchApps(), fetchStats()]);
            setLoading(false);
        };
        init();
    }, [fetchAppDetails, fetchApps, fetchStats]);

    const isNewApp = schemaCount === 0 && connectionCount === 0;

    return (
        <DataBuilderV2Context.Provider value={{
            appId: appId || '',
            oragID: oragID || '',
            appName,
            appFullName,
            apps,
            schemaCount,
            connectionCount,
            isNewApp,
            loading,
            refreshStats,
        }}>
            {children}
        </DataBuilderV2Context.Provider>
    );
};

export default DataBuilderV2Context;
